import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import 'ol/ol.css';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Icon, Style } from 'ol/style';
import Overlay from 'ol/Overlay';
import { BASE_URL } from "../../config";
import "./glass-admin.css";

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_KEYS  = MONTH_NAMES.map(m => m.toLowerCase());

// ── Parse a single "Mon YYYY" token ────────────────────────────────────────
function parseToken(s) {
  s = s.trim();
  const match = s.match(/^([a-zA-Z]+)\s+(\d{4})$/);
  if (!match) return null;
  const mIdx = MONTH_KEYS.findIndex(m => match[1].toLowerCase().startsWith(m));
  const yr   = parseInt(match[2]);
  if (mIdx >= 0 && yr >= 2000) return { month: mIdx, year: yr };
  return null;
}

// ── Parse either a range ("Jan 2025 – Mar 2025") or single ("Feb 2025") ───
function parseMonthRange(raw) {
  if (!raw || !raw.trim()) return { ok: true, from: null, to: null, label: '' };

  const parts = raw.split(/\s*[–\-]\s*/);

  if (parts.length === 1) {
    // Single month mode
    const a = parseToken(parts[0]);
    if (!a) return { ok: false };
    return { ok: true, from: a, to: a, label: `${MONTH_NAMES[a.month]} ${a.year}` };
  }

  if (parts.length === 2) {
    const a = parseToken(parts[0]);
    const b = parseToken(parts[1]);
    if (!a || !b) return { ok: false };
    const aVal = a.year * 12 + a.month;
    const bVal = b.year * 12 + b.month;
    const [from, to] = aVal <= bVal ? [a, b] : [b, a];
    return {
      ok: true, from, to,
      label: `${MONTH_NAMES[from.month]} ${from.year} – ${MONTH_NAMES[to.month]} ${to.year}`,
    };
  }

  return { ok: false };
}

// ── Filter helper ──────────────────────────────────────────────────────────
function filterFeatures(features, mp) {
  return features.filter(f => {
    if (mp.ok && mp.from !== null && mp.to !== null) {
      const m = f.get('month');
      const y = f.get('year');
      if (m === null || y === null) return false;
      const val      = y * 12 + m;
      const fromVal  = mp.from.year * 12 + mp.from.month;
      const toVal    = mp.to.year   * 12 + mp.to.month;
      return val >= fromVal && val <= toVal;
    }
    return true;
  });
}

const OpenLayersMap = () => {
  const mapRef             = useRef(null);
  const overlayRef         = useRef(null);
  const overlayContentRef  = useRef(null);
  const overlayCloserRef   = useRef(null);
  const vectorSourceRef    = useRef(null);
  const allFeaturesRef     = useRef([]);
  const monthRangeRef      = useRef('');

  const [totalCount, setTotalCount] = useState(0);
  const [monthRange, setMonthRange] = useState('');
  const [mode, setMode]             = useState('range'); // 'range' | 'single'

  const mp = parseMonthRange(monthRange);
  monthRangeRef.current = monthRange;

  const placeholder = mode === 'single' ? 'e.g. Feb 2025' : 'e.g. Jan 2025 – Mar 2025';

  // ── Map init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() })],
      view: new View({ center: fromLonLat([124.1667, 9.8333]), zoom: 12 }),
    });

    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;
    map.addLayer(new VectorLayer({ source: vectorSource }));

    const overlay = new Overlay({
      element: overlayRef.current,
      autoPan: { animation: { duration: 250 } },
    });
    map.addOverlay(overlay);

    axios.get(`${BASE_URL}/lisa_survey_irrigation/get_all`).then(response => {
      const data = response.data;
      if (data.status && Array.isArray(data.data)) {
        data.data.forEach(item => {
          const { longitude, latitude, status: farmerStatus, image, farmers_name, created_at } = item;
          if (longitude && latitude && image?.data) {
            const d = created_at ? new Date(created_at) : null;
            const feature = new Feature({
              geometry:    new Point(fromLonLat([parseFloat(longitude), parseFloat(latitude)])),
              imageData:   image.data,
              farmersName: farmers_name || 'Unknown Farmer',
              Status:      farmerStatus || 'Unknown Status',
              month:       d ? d.getMonth() : null,
              year:        d ? d.getFullYear() : null,
            });
            feature.setStyle(new Style({
              image: new Icon({
                src:    'https://openlayers.org/en/latest/examples/data/icon.png',
                anchor: [0.5, 1],
              }),
            }));
            vectorSource.addFeature(feature);
            allFeaturesRef.current.push(feature);
          }
        });
        setTotalCount(data.data.length);
      }
    }).catch(err => console.error('Error fetching data:', err));

    map.on('singleclick', evt => {
      const feature = map.forEachFeatureAtPixel(evt.pixel, f => f);
      if (feature) {
        const imageData   = feature.get('imageData');
        const farmersName = feature.get('farmersName');
        const Status      = feature.get('Status');
        if (imageData) {
          const base64String = btoa(String.fromCharCode(...imageData));
          const imageSrc     = `data:image/jpeg;base64,${base64String}`;
          overlayContentRef.current.innerHTML = `
            <div class="glass-popup-card">
              <div class="glass-popup-header">
                <div class="glass-popup-title">Farmer Details</div>
                <div class="glass-popup-subtitle">${farmersName}</div>
              </div>
              <div class="glass-popup-body">
                <div class="glass-popup-row"><span>Status</span><strong>${Status}</strong></div>
              </div>
              <div class="glass-popup-imagewrap">
                <img src="${imageSrc}" alt="Survey Image" class="glass-popup-image" />
              </div>
            </div>`;
          overlay.setPosition(evt.coordinate);
        }
      } else {
        overlay.setPosition(undefined);
        overlayCloserRef.current.blur();
      }
    });

    overlayCloserRef.current.onclick = () => {
      overlay.setPosition(undefined);
      overlayCloserRef.current.blur();
      const mp = parseMonthRange(monthRangeRef.current);
      vectorSourceRef.current.clear();
      vectorSourceRef.current.addFeatures(filterFeatures(allFeaturesRef.current, mp));
      return false;
    };

    return () => map.setTarget(null);
  }, []);

  // ── Re-filter whenever input changes ─────────────────────────────────────
  useEffect(() => {
    if (!vectorSourceRef.current) return;
    const mp = parseMonthRange(monthRange);
    vectorSourceRef.current.clear();
    vectorSourceRef.current.addFeatures(filterFeatures(allFeaturesRef.current, mp));
  }, [monthRange]);

  // ── Clear input when mode switches ───────────────────────────────────────
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setMonthRange('');
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const tabBase = {
    flex: 1, height: 26, fontSize: 11, border: 'none',
    borderRadius: 5, cursor: 'pointer', fontWeight: 600,
    transition: 'background 0.15s, color 0.15s',
  };

  return (
    <div style={{ position: 'relative' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100vh' }} />

      {/* ── Panel ── */}
      <div style={{
        position: 'absolute', top: 12, right: 12, zIndex: 1000,
        background: 'rgba(255,255,255,0.97)',
        border: '0.5px solid rgba(0,0,0,0.13)',
        borderRadius: 10, padding: '12px 14px',
        minWidth: 290, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: 8, borderBottom: '0.5px solid rgba(0,0,0,0.08)', marginBottom: 10,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#222',
            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Filter by Month
          </span>
          <span style={{ fontSize: 11, color: '#888' }}>
            Total: <strong style={{ color: '#222' }}>{totalCount}</strong>
          </span>
        </div>

        {/* Mode tabs */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 10,
          background: '#f5f5f5', borderRadius: 7, padding: 3,
        }}>
          <button
            onClick={() => handleModeSwitch('range')}
            style={{
              ...tabBase,
              background: mode === 'range' ? '#0F6E56' : 'transparent',
              color: mode === 'range' ? '#fff' : '#555',
            }}
          >
            Range
          </button>
          <button
            onClick={() => handleModeSwitch('single')}
            style={{
              ...tabBase,
              background: mode === 'single' ? '#0F6E56' : 'transparent',
              color: mode === 'single' ? '#fff' : '#555',
            }}
          >
            Single Month
          </button>
        </div>

        {/* Input row */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <input
            type="text"
            placeholder={placeholder}
            maxLength={mode === 'single' ? 9 : 22}
            value={monthRange}
            onChange={e => setMonthRange(e.target.value)}
            style={{
              flex: 1, height: 30, fontSize: 12, padding: '0 8px',
              border: `1px solid ${!mp.ok ? '#E24B4A' : mp.label ? '#0F6E56' : 'rgba(0,0,0,0.18)'}`,
              borderRadius: 6, outline: 'none', background: '#fff', color: '#222',
            }}
          />
          {monthRange && (
            <button
              onClick={() => setMonthRange('')}
              title="Clear filter"
              style={{
                height: 30, padding: '0 10px', fontSize: 12,
                background: '#f5f5f5', border: '1px solid #ddd',
                borderRadius: 6, cursor: 'pointer', color: '#555',
                fontWeight: 600,
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Hint / label */}
        <p style={{
          fontSize: 11, margin: 0, minHeight: 16,
          color: mp.ok && mp.label ? '#0F6E56' : !mp.ok ? '#E24B4A' : '#bbb',
        }}>
          {mp.ok && mp.label
            ? `Showing: ${mp.label}`
            : !mp.ok
            ? mode === 'single'
              ? 'Invalid — try: Feb 2025'
              : 'Invalid — try: Jan 2025 – Mar 2025'
            : mode === 'single'
              ? 'Type a month and year to show only that month\'s surveys.'
              : 'Type a range to filter surveys by period.'}
        </p>
      </div>

      {/* ── Popup ── */}
      <div ref={overlayRef} className="ol-popup glass-popup">
        <button type="button" aria-label="Close popup" ref={overlayCloserRef} className="ol-popup-closer">×</button>
        <div ref={overlayContentRef}></div>
      </div>
    </div>
  );
};

export default OpenLayersMap;